<template>
  <div class="flex flex-col items-start w-64 rounded-lg shadow-xl bg-slate-bright border border-gray-700 p-1">
    <div class="p-2">
      <div v-if="props.boardId" class="flex flex-row items-center w-full justify-between">
        <p class="capitalize text-white">{{ t('highlight') }}</p>
        <div class="flex flex-row w-full justify-end">
          <button
            class="bg-slate-bright rounded-full h-10 w-10 ml-1 cursor-pointer border border-gray-500"
            :class="{
              'border-2 border-green-400': highlightColour.includes('bg-slate') || highlightColour == '',
            }"
            @pointerdown.stop="updateHighlight('bg-slate-bright text-slate-white border-white-dark')"
          ></button>
          <button
            class="bg-blue-middle rounded-full h-10 w-10 ml-2 cursor-pointer border border-gray-500"
            :class="{
              'border-2 border-green-400': highlightColour.includes('bg-blue'),
            }"
            @pointerdown.stop="updateHighlight('bg-blue-middle text-slate-white')"
          ></button>
          <button
            class="bg-white-bright rounded-full h-10 w-10 ml-2 cursor-pointer"
            :class="{
              'border-2 border-green-400': highlightColour.includes('bg-white'),
            }"
            @pointerdown.stop="updateHighlight('bg-white-bright text-slate-dark')"
          ></button>
        </div>
      </div>
      <div class="flex flex-row w-full mt-4">
        <div class="flex flex-row flex-wrap w-full items-start justify-around">
          <button
            v-if="props.boardId"
            class="ml-2 mt-2 cursor-pointer capitalize border border-gray-500 rounded-xl p-2 text-white"
            @pointerdown.stop="duplicateNote()"
          >
            {{ t('duplicate') }}
          </button>
          <button
            v-if="copyIsPossible && props.boardId"
            class="ml-2 mt-2 cursor-pointer capitalize border border-gray-500 rounded-xl p-2 text-white"
            @pointerdown.stop="copyNote()"
          >
            {{ t('copy') }}
          </button>
          <button
            v-if="props.note.attachment?.type === AttachmentType.image"
            class="ml-2 mt-2 cursor-pointer capitalize border border-gray-500 rounded-xl p-2 text-white"
            @pointerdown.stop="showImagePopup()"
          >
            {{ t('fullsize') }}
          </button>
          <button
            v-if="props.boardId"
            class="ml-2 mt-2 cursor-pointer capitalize border border-gray-500 rounded-xl p-2 text-white"
            @pointerdown.stop="unpinNote()"
          >
            {{ t('remove') }}
          </button>
          <button
            v-if="deleteActionAllowed"
            class="ml-2 mt-2 cursor-pointer capitalize border border-gray-500 rounded-xl p-2 text-white"
            @pointerdown.stop="deleteNote()"
          >
            {{ t('delete') }}
          </button>
          <button
            v-if="props.boardId"
            type="button"
            class="ml-2 mt-2 cursor-pointer border border-gray-500 rounded-xl p-2 text-white"
            :class="{ 'border-amber-400': selected }"
            :aria-pressed="selected"
            @click.stop="toggleSelection()"
          >
            {{ t(selected ? 'deselect' : 'select') }}
          </button>
        </div>
      </div>
    </div>
    <div class="w-full h-2 flex flex-row items-center justify-start opacity-70">
      <div
        class="border border-blue-300"
        :class="{
          'border-blue-400': closeTimer < 50,
          'border-orange-400': closeTimer >= 50 && closeTimer < 85,
          'border-red-400': closeTimer >= 85,
        }"
        :style="{
          width: `${100 - closeTimer}%`,
        }"
      ></div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { PropType, computed, onMounted, onUnmounted, ref } from 'vue'
  import { useI18n } from 'vue-i18n'

  import { ParcelType, PermissionLevel, AttachmentType } from '@/constants'
  import { Note, Parcel } from '@/common/classes'

  import { useNoteStore } from '@/composables/noteStore'
  import useWallStore from '@/composables/wallStore'
  import { useUserStore } from '@/composables/userStore'

  import useParcelStore from '@/composables/parcel'
  import useCommonStore from '@/composables/common'
  import useNoteSelection from '@/composables/noteSelection'

  const messages = {
    no: {
      highlight: 'Fremhev',
      select: 'Velg',
      deselect: 'Fjern valg',
    },
    en: {
      highlight: 'highlight',
      select: 'Select',
      deselect: 'Deselect',
    },
  }

  const parcelStore = useParcelStore()
  const wallStore = useWallStore()
  const noteStore = useNoteStore()
  const userStore = useUserStore()
  const common = useCommonStore()
  const noteSelection = useNoteSelection()

  const props = defineProps({
    note: { type: Object as PropType<Note>, required: true },
    boardId: { type: String, required: false, default: null },
    wallId: { type: String, required: false, default: '' },
  })

  const emit = defineEmits<{
    (e: 'deleteNote'): void
    (e: 'closeMenu'): void
    (e: 'showImagePopup', note: Note): void
  }>()

  const highlightColour = computed(() => {
    return props.note.entries[props.boardId].highlightColor
  })

  const copyIsPossible = computed(() => {
    const wall = wallStore.getters.selectedWall.value
    if (wall) return wall.tasks.length > 1
    else return false
  })

  const deleteActionAllowed = computed(() => {
    const wall = wallStore.getters.selectedWall.value
    const user = userStore.getters.user.value
    if (user && wall) {
      return props.note.status.createdBy.user_id === user._id || common.actions.hasMinimumPermission(user, PermissionLevel.Organize, wall)
    } else return false
  })

  const selected = computed(() => noteSelection.getters.isSelected(props.note._id))

  function toggleSelection() {
    noteSelection.actions.toggleNote(props.note._id)
    emit('closeMenu')
  }

  const menuStyles = computed(() => {
    return props.boardId ? 'text-slate-white py-3 px-4' : 'p-5'
  })

  const closeTimer = ref(0)
  let timerInterval: NodeJS.Timeout

  onMounted(() => {
    closeTimer.value = 0
    timerInterval = setInterval(() => {
      closeTimer.value += 1
      if (closeTimer.value === 100) emit('closeMenu')
    }, 100)
  })

  onUnmounted(() => {
    clearInterval(timerInterval)
  })

  const { t } = useI18n({ messages })

  function copyNote() {
    emit('closeMenu')
    const copyNote = new Note(props.note)
    noteStore.actions.stageNoteCopy(copyNote)
  }

  //Updated duplicate note to make duplication of notes also appear on other participants sidebars.
  async function duplicateNote() {
    emit('closeMenu')
    // duplication means to create an exact replica of the note, without copying the boardEntries
    // this can only be done within the same task -> copy / paste is used for cross task

    const wall = wallStore.getters.selectedWall.value
    if (!wall) return //Check if the wall object is there

    const dupNote = new Note(props.note)
    dupNote.wall_id = wall._id //Need to store wall id in dupNote before passing it into a parcel
    dupNote.resetBoardEntries() // ensure that the note is not pinned

    const res = await noteStore.actions.addNewNote(dupNote)
    if (res.success && res.body) {
      const savedNote = res.body as Note
      const parcel = new Parcel({ parcelType: ParcelType.MessageCreate, body: savedNote }, userStore.getters.user.value, wall)
      await parcelStore.actions.sendParcel(wall._id, parcel)
    }
  }

  async function unpinNote() {
    emit('closeMenu')
    const wall = wallStore.getters.selectedWall.value
    if (props.wallId && wall) {
      let note = new Note(props.note)
      delete note.entries[props.boardId] // remove entry from object
      const res = await noteStore.actions.updateNote(note, props.wallId, ParcelType.MessageBoard)
      if (res.success) {
        const parcel = new Parcel({ parcelType: ParcelType.MessageBoard, body: note }, userStore.getters.user.value, wall)
        await parcelStore.actions.sendParcel(props.wallId, parcel)
      }
    }
  }

  function deleteNote() {
    emit('deleteNote')
  }

  async function updateHighlight(color: string) {
    emit('closeMenu')
    let updatedNote = new Note(props.note)
    updatedNote.setHighlightColor(color, props.boardId)

    const wall = wallStore.getters.selectedWall.value
    if (props.wallId && wall) {
      const res = await noteStore.actions.updateNote(updatedNote, props.wallId, ParcelType.MessageBoard)
      if (res.success) {
        const parcel = new Parcel({ parcelType: ParcelType.MessageBoard, body: updatedNote }, userStore.getters.user.value, wall)
        await parcelStore.actions.sendParcel(props.wallId, parcel)
      }
    }
  }

  function showImagePopup() {
    emit('showImagePopup', props.note)
  }
</script>

<style scoped></style>
